import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import * as store from "@/lib/storage";
import { buildReport } from "@/lib/calculations";
import { generateRecommendations } from "@/lib/recommendations";
import { api } from "@/lib/api";
import { getVisitorId } from "@/lib/analytics";
import { useAuth } from "@/context/AuthContext";

const AppContext = createContext(null);

export function AppProvider({ children }) {
  const { isAuthenticated, user } = useAuth();
  const [assessment, setAssessment] = useState(() => store.loadAssessment());
  const [report, setReport] = useState(() => store.loadCurrentReport());
  const [entitlement, setEntitlement] = useState(() => store.loadEntitlement());
  const [subscription, setSubscription] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [history, setHistory] = useState(() => store.loadHistory());
  const [synced, setSynced] = useState(() => store.loadSyncedMap());
  const [now, setNow] = useState(() => Date.now());

  // Tick every minute so expiry state stays fresh without aggressive UI.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(t);
  }, []);

  // Server-side premium subscription (tied to the logged-in account).
  const refreshSubscription = useCallback(async () => {
    if (!isAuthenticated) {
      setSubscription(null);
      return null;
    }
    try {
      const s = await api.subscriptionMe();
      setSubscription(s);
      return s;
    } catch {
      setSubscription(null);
      return null;
    }
  }, [isAuthenticated]);

  // Refetch whenever auth state OR the logged-in account changes (switching
  // accounts without a full logout must not inherit the previous user's premium).
  // Guarded so a slow response for a previous account can't land after a switch.
  useEffect(() => {
    let cancelled = false;
    setSubscription(null); // clear stale entitlement before the new account loads
    if (isAuthenticated) {
      setSubscriptionLoading(true);
      api
        .subscriptionMe()
        .then((s) => { if (!cancelled) setSubscription(s); })
        .catch(() => { if (!cancelled) setSubscription(null); })
        .finally(() => { if (!cancelled) setSubscriptionLoading(false); });
    } else {
      setSubscriptionLoading(false);
    }
    return () => { cancelled = true; };
  }, [isAuthenticated, user?.id]);

  const saveAssessment = useCallback((data) => {
    store.saveAssessment(data);
    setAssessment(data);
  }, []);

  // Generate + persist a report from assessment data.
  const generateReport = useCallback((data) => {
    const rpt = buildReport(data);
    store.saveCurrentReport(rpt);
    store.addHistorySnapshot(rpt);
    setReport(rpt);
    setHistory(store.loadHistory());
    return rpt;
  }, []);

  const unlockPremium = useCallback(() => {
    const reportId = report ? report.id : null;
    const ent = store.unlockPremium(reportId);
    setEntitlement(ent);
    return ent;
  }, [report]);

  // Update financial data (only allowed while simulation active).
  const updateData = useCallback((data) => {
    store.saveAssessment(data);
    setAssessment(data);
    const rpt = buildReport(data);
    store.saveCurrentReport(rpt);
    store.addHistorySnapshot(rpt);
    setReport(rpt);
    setHistory(store.loadHistory());
    // keep entitlement pointing to latest report id
    const ent = store.loadEntitlement();
    if (ent && ent.premium) {
      const updated = { ...ent, reportId: rpt.id };
      store.saveEntitlement(updated);
      setEntitlement(updated);
    }
    return rpt;
  }, []);

  // Sync a PRIVACY-SAFE derived report to the backend. Never sends raw
  // financial inputs (salaries/debts/expenses) or absolute rupiah amounts;
  // recommendation rupiahs are redacted server-side. Ratios/months/points only.
  const syncReport = useCallback(async () => {
    if (!report) return null;
    const { base, hero } = report;
    const recs = generateRecommendations(base, report.data).recommendations || [];
    const payload = {
      visitorId: getVisitorId(),
      score: base.score,
      status: base.status,
      components: base.scoreBreakdown,
      safetyOverride: !!base.safetyOverride,
      emergencyMonths: Number(base.emergencyMonths) || 0,
      debtToCoreIncome: Number(base.debtToCoreIncome) || 0,
      sideDependency: Number(base.sideDependency) || 0,
      bands: { status: base.status },
      runway: {
        survivalRunway: hero.after.survivalRunway,
        isPositive: !!hero.after.isPositive,
      },
      recommendations: recs,
      entitlement: { premium: store.isPremium(store.loadEntitlement()) },
    };
    const res = await api.saveReport(payload);
    try {
      store.markSynced(report.id, res && res.id);
      setSynced(store.loadSyncedMap());
    } catch {
      /* non-fatal */
    }
    return res;
  }, [report]);

  const resetAll = useCallback(() => {
    store.resetAll();
    setAssessment(null);
    setReport(null);
    setEntitlement(null);
    setHistory([]);
    setSynced({});
  }, []);

  const premium = !!(subscription && subscription.active);
  const subscriptionUntil = subscription && subscription.activeUntil ? Date.parse(subscription.activeUntil) : null;
  const simulationActive = premium; // active subscription = full simulation access

  const value = {
    assessment,
    report,
    entitlement,
    subscription,
    subscriptionUntil,
    subscriptionLoading,
    history,
    synced,
    now,
    premium,
    simulationActive,
    saveAssessment,
    generateReport,
    updateData,
    unlockPremium,
    refreshSubscription,
    resetAll,
    syncReport,
    refreshHistory: () => setHistory(store.loadHistory()),
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used within AppProvider");
  return ctx;
}
